/**
 * NameTranslationsField — мультиязычный ввод с табами языков.
 *
 * value = `name_translations` JSONB: { ru: '…', en: '…', … }.
 * Семантика валидации: ≥1 язык должен быть непустым (FR-2). Контролируется
 * родителем — мы только показываем, родитель собирает errors.
 *
 * Поддерживаемые языки v1: ru, en, lat (научное). Расширение —
 * добавить в LOCALES (или сделать дин. список из настроек, но пока v1).
 */
import { useState } from 'react';
import { Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui/tabs';

const LOCALES: { code: string; label: string; flag: string }[] = [
  { code: 'ru', label: 'Русский', flag: 'RU' },
  { code: 'en', label: 'English', flag: 'EN' },
  { code: 'lat', label: 'Латинское', flag: 'LAT' },
];

export interface NameTranslationsFieldProps {
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  /** Какие коды локалей показывать. По умолчанию все из LOCALES. */
  locales?: string[];
  /** Disabled = readonly. */
  disabled?: boolean;
  /** Подсказка под полем (требование «≥1 язык»). */
  hint?: string;
  hasError?: boolean;
}

export function NameTranslationsField({
  value,
  onChange,
  locales,
  disabled,
  hint,
  hasError,
}: NameTranslationsFieldProps) {
  const shown = LOCALES.filter((l) => !locales || locales.includes(l.code));
  const [active, setActive] = useState<string>(shown[0]?.code ?? 'ru');

  const handle = (code: string, v: string) => {
    onChange({ ...value, [code]: v });
  };

  return (
    <div>
      <Tabs value={active} onValueChange={setActive}>
        <TabsList className="mb-3">
          {shown.map((l) => {
            const filled = (value[l.code] ?? '').trim().length > 0;
            return (
              <TabsTrigger key={l.code} value={l.code} className="gap-1.5">
                <span className="text-[10px] font-semibold opacity-70">
                  {l.flag}
                </span>
                <span>{l.label}</span>
                {filled && (
                  <span
                    aria-hidden
                    className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-primary"
                  />
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {shown.map((l) => (
          <TabsContent key={l.code} value={l.code} className="mt-0">
            <Input
              value={value[l.code] ?? ''}
              onChange={(e) => handle(l.code, e.target.value)}
              disabled={disabled}
              placeholder={`Название (${l.label})`}
              className={cn(hasError && 'border-destructive focus-visible:ring-destructive')}
            />
          </TabsContent>
        ))}
      </Tabs>

      <div className="mt-2 flex items-center justify-between">
        <p
          className={cn(
            'text-xs',
            hasError ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {hint ?? 'Требуется минимум 1 язык.'}
        </p>
        <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
          <Plus className="h-3 w-3" />
          добавить язык (скоро)
        </span>
      </div>
    </div>
  );
}
